/**
 * Shared state for the Inventory Audit Data Table.
 *
 * Tracks the currently-viewed sheet, selected run, pagination state, and
 * cached column config. Future commits will extend this with filters, sort,
 * and selected vehicle (for the detail drawer).
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react"
import { useSearchParams } from "react-router-dom"

import {
  AuditColumnConfig,
  FilterOptionsResponse,
  VehicleResponse,
  getColumnConfig,
  getFilterOptions,
  listVehicles,
} from "../api/audit"

export const KNOWN_SHEETS = [
  "discrepancies",
  "pending",
  "completed",
  "unclassified",
  "dealer_duplicates",
] as const

export type SheetSlug = typeof KNOWN_SHEETS[number]

export const DEFAULT_SHEET: SheetSlug = "discrepancies"

function parseSheetFromUrl(raw: string | null): SheetSlug {
  if (raw && (KNOWN_SHEETS as readonly string[]).includes(raw)) {
    return raw as SheetSlug
  }
  return DEFAULT_SHEET
}
export const DEFAULT_PAGE = 1
export const DEFAULT_SIZE = 50

// ---------------------------------------------------------------------------
// Filter / Sort / Search types & URL parsers
// ---------------------------------------------------------------------------

export interface FilterState {
  rooftop: string[]
  vehicle_type: string[]
  flag_type: string[]
}

export interface SortState {
  field: string
  direction: "asc" | "desc"
}

const EMPTY_FILTERS: FilterState = {
  rooftop: [],
  vehicle_type: [],
  flag_type: [],
}

function parseFilterFromUrl(raw: string | null): string[] {
  if (!raw) return []
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
}

function parseSortFromUrl(raw: string | null): SortState | null {
  if (!raw) return null
  const parts = raw.split(":")
  if (parts.length !== 2) return null
  const [field, direction] = parts
  if (direction !== "asc" && direction !== "desc") return null
  if (!field) return null
  return { field, direction }
}

function parseSearchFromUrl(raw: string | null): string {
  return raw ?? ""
}

interface AuditContextValue {
  currentSheet: SheetSlug
  setCurrentSheet: (sheet: SheetSlug) => void
  currentRunId: number | null
  setCurrentRunId: (id: number | null) => void
  page: number
  setPage: (page: number) => void
  size: number
  setSize: (size: number) => void
  columnConfig: AuditColumnConfig[]
  columnConfigLoading: boolean
  columnConfigError: string | null
  loadColumnsForSheet: (sheet: string) => Promise<void>
  // Vehicle data state
  vehicles: VehicleResponse[]
  totalMatching: number
  totalUnfiltered: number
  vehiclesLoading: boolean
  vehiclesError: string | null
  loadVehicles: () => Promise<void>
  // Filters
  filters: FilterState
  setFilter: (key: keyof FilterState, values: string[]) => void
  // Sort
  sort: SortState | null
  setSort: (next: SortState | null) => void
  cycleSort: (field: string) => void
  // Search
  search: string
  setSearch: (next: string) => void
  // Clear all filters/sort/search
  clearAll: () => void
  // Filter options (populated from API per run+sheet)
  filterOptions: FilterOptionsResponse | null
  filterOptionsLoading: boolean
  filterOptionsError: string | null
  // User-resized column widths (field name → pixel width). Overrides backend hints.
  resizedWidths: Record<string, number>
  setColumnWidth: (field: string, width: number) => void
  resetColumnWidths: () => void
}

const AuditContext = createContext<AuditContextValue | undefined>(undefined)

export const AuditProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [currentSheet, setCurrentSheetState] = useState<SheetSlug>(() =>
    parseSheetFromUrl(searchParams.get("sheet"))
  )

  // Keep URL in sync when sheet changes — also clear filters/sort/search
  const setCurrentSheet = useCallback((sheet: SheetSlug) => {
    setCurrentSheetState(sheet)
    setFiltersState(EMPTY_FILTERS)
    setSortState(null)
    setSearchState("")
    setPage(1)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (sheet === DEFAULT_SHEET) {
        next.delete("sheet")
      } else {
        next.set("sheet", sheet)
      }
      // Strip filter/sort/search params on sheet change
      next.delete("rooftop")
      next.delete("vehicle_type")
      next.delete("flag_type")
      next.delete("sort")
      next.delete("q")
      return next
    }, { replace: true })
  }, [setSearchParams])
  const [currentRunId, setCurrentRunId] = useState<number | null>(null)
  const [page, setPage] = useState<number>(DEFAULT_PAGE)
  const [size, setSize] = useState<number>(DEFAULT_SIZE)
  const [columnConfig, setColumnConfig] = useState<AuditColumnConfig[]>([])
  const [columnConfigLoading, setColumnConfigLoading] = useState<boolean>(false)
  const [columnConfigError, setColumnConfigError] = useState<string | null>(null)
  const [vehicles, setVehicles] = useState<VehicleResponse[]>([])
  const [totalMatching, setTotalMatching] = useState<number>(0)
  const [totalUnfiltered, setTotalUnfiltered] = useState<number>(0)
  const [vehiclesLoading, setVehiclesLoading] = useState<boolean>(false)
  const [vehiclesError, setVehiclesError] = useState<string | null>(null)
  // Filter/sort/search state — seeded from URL on mount
  const [filters, setFiltersState] = useState<FilterState>(() => ({
    rooftop: parseFilterFromUrl(searchParams.get("rooftop")),
    vehicle_type: parseFilterFromUrl(searchParams.get("vehicle_type")),
    flag_type: parseFilterFromUrl(searchParams.get("flag_type")),
  }))
  const [sort, setSortState] = useState<SortState | null>(() =>
    parseSortFromUrl(searchParams.get("sort"))
  )
  const [search, setSearchState] = useState<string>(() =>
    parseSearchFromUrl(searchParams.get("q"))
  )
  const [filterOptions, setFilterOptions] = useState<FilterOptionsResponse | null>(null)
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false)
  const [filterOptionsError, setFilterOptionsError] = useState<string | null>(null)

  const [resizedWidths, setResizedWidths] = useState<Record<string, number>>({})

  const setColumnWidth = useCallback((field: string, width: number) => {
    setResizedWidths((prev) => ({ ...prev, [field]: width }))
  }, [])

  const resetColumnWidths = useCallback(() => {
    setResizedWidths({})
  }, [])

  // Reset user-resized widths when sheet changes (different sheets have different columns)
  useEffect(() => {
    setResizedWidths({})
  }, [currentSheet])

  // ---------------------------------------------------------------------------
  // URL sync helper
  // ---------------------------------------------------------------------------

  const syncParamsToUrl = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        for (const [key, value] of Object.entries(updates)) {
          if (value === null || value === "") {
            next.delete(key)
          } else {
            next.set(key, value)
          }
        }
        return next
      }, { replace: true })
    },
    [setSearchParams]
  )

  // ---------------------------------------------------------------------------
  // Filter / Sort / Search setters
  // ---------------------------------------------------------------------------

  const setFilter = useCallback(
    (key: keyof FilterState, values: string[]) => {
      setFiltersState((prev) => ({ ...prev, [key]: values }))
      setPage(1)
      syncParamsToUrl({
        [key]: values.length > 0 ? values.join(",") : null,
      })
    },
    [syncParamsToUrl]
  )

  const setSort = useCallback(
    (next: SortState | null) => {
      setSortState(next)
      setPage(1)
      syncParamsToUrl({
        sort: next ? `${next.field}:${next.direction}` : null,
      })
    },
    [syncParamsToUrl]
  )

  const cycleSort = useCallback(
    (field: string) => {
      setSortState((prev) => {
        let next: SortState | null
        if (!prev || prev.field !== field) {
          next = { field, direction: "asc" }
        } else if (prev.direction === "asc") {
          next = { field, direction: "desc" }
        } else {
          next = null
        }
        syncParamsToUrl({
          sort: next ? `${next.field}:${next.direction}` : null,
        })
        return next
      })
      setPage(1)
    },
    [syncParamsToUrl]
  )

  const setSearch = useCallback(
    (next: string) => {
      setSearchState(next)
      setPage(1)
      syncParamsToUrl({
        q: next.trim() === "" ? null : next,
      })
    },
    [syncParamsToUrl]
  )

  const clearAll = useCallback(() => {
    setFiltersState(EMPTY_FILTERS)
    setSortState(null)
    setSearchState("")
    setPage(1)
    syncParamsToUrl({
      rooftop: null,
      vehicle_type: null,
      flag_type: null,
      sort: null,
      q: null,
    })
  }, [syncParamsToUrl])

  const loadColumnsForSheet = useCallback(async (sheet: string) => {
    setColumnConfigLoading(true)
    setColumnConfigError(null)
    try {
      const response = await getColumnConfig(sheet)
      setColumnConfig(response.columns)
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load column config"
      setColumnConfigError(message)
      setColumnConfig([])
    } finally {
      setColumnConfigLoading(false)
    }
  }, [])

  const loadVehicles = useCallback(async () => {
    // Guard: need a selected run before fetching
    if (currentRunId === null) {
      setVehicles([])
      setTotalMatching(0)
      setTotalUnfiltered(0)
      return
    }
    setVehiclesLoading(true)
    setVehiclesError(null)
    try {
      const params: Parameters<typeof listVehicles>[1] = {
        sheet: currentSheet,
        page,
        size,
      }
      if (filters.rooftop.length > 0) params.rooftop = filters.rooftop.join(",")
      if (filters.vehicle_type.length > 0) params.vehicle_type = filters.vehicle_type.join(",")
      if (filters.flag_type.length > 0) params.flag_type = filters.flag_type.join(",")
      if (sort) params.sort = `${sort.field}:${sort.direction}`
      if (search.trim() !== "") params.search = search.trim()

      const response = await listVehicles(currentRunId, params)
      setVehicles(response.vehicles)
      setTotalMatching(response.total_matching)
      setTotalUnfiltered(response.total_unfiltered)
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load vehicles"
      setVehiclesError(message)
      setVehicles([])
      setTotalMatching(0)
      setTotalUnfiltered(0)
    } finally {
      setVehiclesLoading(false)
    }
  }, [currentRunId, currentSheet, page, size, filters, sort, search])

  // Load filter options when run or sheet changes
  useEffect(() => {
    if (currentRunId === null) {
      setFilterOptions(null)
      return
    }
    let cancelled = false
    setFilterOptionsLoading(true)
    setFilterOptionsError(null)
    getFilterOptions(currentRunId, currentSheet)
      .then((options) => {
        if (!cancelled) setFilterOptions(options)
      })
      .catch((err) => {
        if (!cancelled) {
          setFilterOptionsError(
            err instanceof Error ? err.message : "Failed to load filter options"
          )
        }
      })
      .finally(() => {
        if (!cancelled) setFilterOptionsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [currentRunId, currentSheet])

  const value = useMemo<AuditContextValue>(
    () => ({
      currentSheet,
      setCurrentSheet,
      currentRunId,
      setCurrentRunId,
      page,
      setPage,
      size,
      setSize,
      columnConfig,
      columnConfigLoading,
      columnConfigError,
      loadColumnsForSheet,
      vehicles,
      totalMatching,
      totalUnfiltered,
      vehiclesLoading,
      vehiclesError,
      loadVehicles,
      filters,
      setFilter,
      sort,
      setSort,
      cycleSort,
      search,
      setSearch,
      clearAll,
      filterOptions,
      filterOptionsLoading,
      filterOptionsError,
      resizedWidths,
      setColumnWidth,
      resetColumnWidths,
    }),
    [
      currentSheet,
      setCurrentSheet,
      currentRunId,
      setCurrentRunId,
      page,
      setPage,
      size,
      setSize,
      columnConfig,
      columnConfigLoading,
      columnConfigError,
      loadColumnsForSheet,
      vehicles,
      totalMatching,
      totalUnfiltered,
      vehiclesLoading,
      vehiclesError,
      loadVehicles,
      filters,
      setFilter,
      sort,
      setSort,
      cycleSort,
      search,
      setSearch,
      clearAll,
      filterOptions,
      filterOptionsLoading,
      filterOptionsError,
      resizedWidths,
      setColumnWidth,
      resetColumnWidths,
    ]
  )

  return <AuditContext.Provider value={value}>{children}</AuditContext.Provider>
}

export const useAudit = (): AuditContextValue => {
  const ctx = useContext(AuditContext)
  if (!ctx) {
    throw new Error("useAudit must be used within AuditProvider")
  }
  return ctx
}
